import React, { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import classNames from 'classnames';
import { z } from 'zod';

import { MainModalController } from '@uikit/components/Modal';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  CustomDialog,
  DialogButton,
  DialogButtonsWrapper,
} from '@ui/uikit/components/common/Dialog';
import { FormInput } from '@ui/uikit/components/common/FormInput';

import { useSwapExperienceStore } from '../utilities/SwapExperienceStore';

interface SlippageModalProps {
  modalController: MainModalController;
  minSlippagePercent?: number;
  maxSlippagePercent?: number;
}

const SlippageModal = ({
  modalController,
  minSlippagePercent = 0,
  maxSlippagePercent = 100,
}: SlippageModalProps) => {
  const store = useSwapExperienceStore();

  const formSchema = useMemo(() => {
    return z.object({
      slippagePercent: z.coerce
        .number()
        .refine((value) => value >= minSlippagePercent, {
          message: `Slippage tolerance must not be negative.`,
        })
        .refine((value) => value <= maxSlippagePercent, {
          message: `Slippage tolerance must not be greater than ${maxSlippagePercent}%.`,
        }),
    });
  }, [minSlippagePercent, maxSlippagePercent]);

  const { handleSubmit, control } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      slippagePercent: store.slippagePercent,
    },
  });

  const handleSubmitForm = useMemo(() => {
    return handleSubmit(({ slippagePercent }) => {
      console.log('Form submitted');

      store.setSlippagePercent(slippagePercent);
      modalController.close();
    });
  }, [handleSubmit, modalController, store]);

  const handleModalOpenChange = useCallback(
    (value: boolean) => {
      if (!value) {
        modalController.close();
      }
    },
    [modalController],
  );

  return (
    <CustomDialog
      title="Slippage Tolerance"
      titleIsLeftAligned
      size="sm"
      isOpen={modalController.isOpen}
      onOpenChange={handleModalOpenChange}
    >
      <form onSubmit={handleSubmitForm} className={classNames('flex flex-col')}>
        <Controller
          control={control}
          name="slippagePercent"
          render={({ field: { ref, onChange, value: fieldValue, ...field }, fieldState }) => {
            const handleValueChange = (value: string) => {
              if (value === '') {
                return;
              }

              const numericValue = Number.parseFloat(value);

              if (numericValue < 0 || numericValue > maxSlippagePercent) {
                return;
              }

              onChange(numericValue.toFixed(1));
            };

            return (
              <FormInput
                inputRef={ref}
                value={fieldValue?.toString()}
                type="number"
                label="Slippage Tolerance (%)"
                step=".1"
                min={minSlippagePercent}
                max={maxSlippagePercent}
                required={true}
                data-qa="slippage-tolerance-input"
                isError={!!fieldState.error}
                hint={fieldState.error?.message}
                onValueChange={handleValueChange}
                {...field}
              />
            );
          }}
        />

        <DialogButtonsWrapper>
          <DialogButton type="submit">Confirm</DialogButton>
        </DialogButtonsWrapper>
      </form>
    </CustomDialog>
  );
};

export default SlippageModal;
